import { useState, useRef } from "react"

import {
    Image,
    FileText,
    Eye,
    ExternalLink,
    X,
    ZoomIn,
    ZoomOut,
    RotateCcw,
} from "lucide-react"

import ModerationWarning from "../../../../Components/ModerationWarning"

// `type` ("image" | "pdf") comes from the API. PDFs cannot be reliably embedded
// across browsers (Chrome refuses to render a PDF inside an <img>), so they are
// opened directly in a new tab instead of inline.
const isPdfType = (type, contentType) =>
    (type || "").toLowerCase() === "pdf" || /pdf/i.test(contentType || "")

// `warning`, when set ({ labels }), renders an amber content-moderation warning above the
// card (e.g. a flagged selfie) — the image itself is still lazily loadable, unchanged.
// `autoLoad` shows the image straight away (no "View" click); `fit="contain"` shows the whole image
// (letterboxed) instead of cropping it to the 4:3 card — e.g. for ID documents.
const LazyImageCard = ({ label, src, type, contentType, warning, autoLoad = false, fit = "cover" }) => {
    const [imgError, setImgError] = useState(false)
    const [isLoaded, setIsLoaded] = useState(autoLoad)
    const [isFullScreen, setIsFullScreen] = useState(false)

    // Zoom / pan state for the full-screen view.
    const [scale, setScale] = useState(1)
    const [offset, setOffset] = useState({ x: 0, y: 0 })
    const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, originX: 0, originY: 0 })

    const MIN_SCALE = 1
    const MAX_SCALE = 5

    const onFullScreen = () => setIsFullScreen(true)

    const resetZoom = () => {
        setScale(MIN_SCALE)
        setOffset({ x: 0, y: 0 })
    }

    const onClose = () => {
        setIsFullScreen(false)
        resetZoom()
    }

    const zoomBy = (delta) => {
        setScale((prev) => {
            const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number((prev + delta).toFixed(2))))
            if (next === MIN_SCALE) setOffset({ x: 0, y: 0 })
            return next
        })
    }

    const handleWheel = (e) => {
        zoomBy(e.deltaY < 0 ? 0.25 : -0.25)
    }

    const handlePointerDown = (e) => {
        if (scale <= MIN_SCALE) return
        dragRef.current = {
            active: true,
            moved: false,
            startX: e.clientX,
            startY: e.clientY,
            originX: offset.x,
            originY: offset.y,
        }
    }

    const handlePointerMove = (e) => {
        if (!dragRef.current.active) return
        const dx = e.clientX - dragRef.current.startX
        const dy = e.clientY - dragRef.current.startY
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true
        setOffset({ x: dragRef.current.originX + dx, y: dragRef.current.originY + dy })
    }

    const handlePointerUp = () => {
        dragRef.current.active = false
    }

    // A drag should not be treated as a backdrop click that closes the view.
    const handleBackdropClick = () => {
        if (dragRef.current.moved) {
            dragRef.current.moved = false
            return
        }
        onClose()
    }

    // PDF: don't embed — render a card that opens the document in a new tab.
    if (isPdfType(type, contentType)) {
        return (
            <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center aspect-[4/3] rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all group"
            >
                <FileText className="w-8 h-8 text-gray-300 mb-3 group-hover:text-blue-400 transition-colors" />
                <p className="text-sm font-semibold text-gray-700 mb-2 text-center px-2">{label}</p>
                <span className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-600 group-hover:border-blue-400 group-hover:text-blue-600 transition-colors shadow-sm">
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Open PDF</span>
                </span>
            </a>
        )
    }

    // State 1: Not loaded yet — show label + View button
    if (!isLoaded) {
        return (
            <div className="flex flex-col gap-1.5">
                {warning && <ModerationWarning labels={warning.labels} />}
                <div
                    onClick={() => setIsLoaded(prev => !prev)}
                    className="flex flex-col items-center justify-center aspect-[4/3] rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all group"
                >
                    <Image className="w-8 h-8 text-gray-300 mb-3 group-hover:text-blue-400 transition-colors" />
                    <p className="text-sm font-semibold text-gray-700 mb-2">{label}</p>
                    <span className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-600 group-hover:border-blue-400 group-hover:text-blue-600 transition-colors shadow-sm">
                        <Eye className="w-3.5 h-3.5" />
                        <span>View</span>
                    </span>
                </div>
            </div>
        )
    }

    // State 2: Load failed
    if (imgError) {
        return (
            <div className="flex flex-col items-center justify-center aspect-[4/3] rounded-lg border-2 border-red-200 bg-red-50">
                <Image className="w-8 h-8 text-red-300 mb-2" />
                <p className="text-sm font-medium text-red-500">{label}</p>
                <p className="text-xs text-red-400 mt-1">Failed to load</p>
            </div>
        )
    }

    // State 3: Image loaded inline — click opens lightbox
    return (
        <>
            <div className="flex flex-col items-center">
                {warning && <div className="w-full mb-2"><ModerationWarning labels={warning.labels} /></div>}
                <p className="text-sm text-gray-500 font-medium mb-2">{label}</p>
                <div
                    onClick={onFullScreen}
                    className="w-full aspect-[4/3] rounded-lg border-2 border-gray-200 overflow-hidden cursor-pointer hover:border-blue-400 hover:shadow-md transition-all group relative bg-gray-100"
                >
                    <img
                        src={src}
                        alt={label}
                        className={`w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"}`}
                        onError={() => setImgError(true)}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-3 py-1 rounded-full">
                            Full screen
                        </span>
                    </div>
                </div>
            </div>

            {isFullScreen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 overflow-hidden"
                    onClick={handleBackdropClick}
                    onWheel={handleWheel}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerLeave={handlePointerUp}
                >
                    <button
                        className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                        onClick={(e) => { e.stopPropagation(); onClose() }}
                    >
                        <X className="w-6 h-6" />
                    </button>

                    {/* ZOOM CONTROLS */}
                    <div
                        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 bg-black/50 rounded-full px-2 py-1.5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="p-1.5 rounded-full text-white hover:bg-white/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            onClick={() => zoomBy(-0.25)}
                            disabled={scale <= MIN_SCALE}
                            aria-label="Zoom out"
                        >
                            <ZoomOut className="w-5 h-5" />
                        </button>
                        <span className="text-white text-sm font-medium w-12 text-center select-none">
                            {Math.round(scale * 100)}%
                        </span>
                        <button
                            className="p-1.5 rounded-full text-white hover:bg-white/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            onClick={() => zoomBy(0.25)}
                            disabled={scale >= MAX_SCALE}
                            aria-label="Zoom in"
                        >
                            <ZoomIn className="w-5 h-5" />
                        </button>
                        <button
                            className="p-1.5 rounded-full text-white hover:bg-white/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            onClick={resetZoom}
                            disabled={scale === MIN_SCALE && offset.x === 0 && offset.y === 0}
                            aria-label="Reset zoom"
                        >
                            <RotateCcw className="w-5 h-5" />
                        </button>
                    </div>

                    <img
                        src={src}
                        alt={label}
                        draggable={false}
                        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl select-none"
                        style={{
                            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                            cursor: scale > MIN_SCALE ? (dragRef.current.active ? "grabbing" : "grab") : "default",
                            transition: dragRef.current.active ? "none" : "transform 0.15s ease-out",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={handlePointerDown}
                    />
                </div>
            )}
        </>
    )
}

export default LazyImageCard
